import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie, faTrash, faMoneyBillWave } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { formatCurrencyCentValue, formatRFCDate } from '@/utils/helpers';
import type { JournalOptionTrade, JournalStockPosition } from '@/models';
import { STRATEGY_LABELS, STOCK_STATUS_LABELS, pnlColorClass } from '@/components/journal/journalHelpers';
import { useJournalResource } from '@/components/journal/useJournalResource';
import { showSuccessToast, showErrorToast } from '@/utils/toasts';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import DataTable from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import EmptyState from '@/components/ui/EmptyState';
import ErrorState from '@/components/ui/ErrorState';
import Modal from '@/components/ui/Modal';

const boundCapital = (t: JournalOptionTrade): number => Number(t.strike) * t.quantity * t.multiplier;

const OPTION_COLUMNS: DataTableColumn<JournalOptionTrade>[] = [
  { key: 'symbol', header: 'Symbol', primary: true, render: (t) => <span className="font-medium text-gray-900 dark:text-white">{t.symbolTicker}</span> },
  { key: 'right', header: 'Typ', render: (t) => t.right },
  { key: 'strike', header: 'Strike', render: (t) => formatCurrencyCentValue(t.strike, t.currency) },
  { key: 'expiry', header: 'Verfall', render: (t) => formatRFCDate(t.expiry) },
  { key: 'quantity', header: 'Menge', align: 'right', render: (t) => t.quantity },
  { key: 'strategy', header: 'Strategie', render: (t) => STRATEGY_LABELS[t.strategy] },
  { key: 'netCredit', header: 'Netto-Credit', align: 'right', render: (t) => formatCurrencyCentValue(t.netCredit, t.currency) },
  {
    key: 'boundCapital',
    header: 'Gebundenes Kapital',
    align: 'right',
    render: (t) => (t.right === 'PUT' && t.direction === 'SELL_TO_OPEN' ? formatCurrencyCentValue(boundCapital(t), t.currency) : '—'),
  },
  {
    key: 'unrealizedPnL',
    header: 'Unrealisiert',
    align: 'right',
    render: (t) => (
      <span className={`font-medium ${pnlColorClass(t.unrealizedPnL)}`}>
        {t.unrealizedPnL === null ? '—' : formatCurrencyCentValue(t.unrealizedPnL, t.currency)}
      </span>
    ),
  },
];

export default function PositionsView() {
  const { data, loading, error, reload } = useJournalResource(
    async () => {
      const [optionsResp, stockResp] = await Promise.all([
        APIRoutes.listJournalOptionTrades({ status: 'OPEN', limit: 200 }),
        APIRoutes.listJournalStockPositions({ status: 'OPEN', limit: 200 }),
      ]);
      return { openOptions: optionsResp.optionTrades, stockPositions: stockResp.stockPositions };
    },
    [],
    'Fehler beim Laden der Positionen',
  );
  const [deleteTarget, setDeleteTarget] = useState<JournalStockPosition | null>(null);
  const [busy, setBusy] = useState(false);
  const openOptions = data?.openOptions ?? [];
  const stockPositions = data?.stockPositions ?? [];

  const handleDeleteStock = async () => {
    if (deleteTarget === null) return;
    setBusy(true);
    try {
      await APIRoutes.deleteJournalStockPosition(deleteTarget.id);
      showSuccessToast('Aktienposition gelöscht');
      setDeleteTarget(null);
      reload();
    } catch (err) {
      console.error('Failed to delete stock position:', err);
      showErrorToast('Aktienposition konnte nicht gelöscht werden');
    } finally {
      setBusy(false);
    }
  };

  const stockColumns: DataTableColumn<JournalStockPosition>[] = [
    { key: 'symbol', header: 'Symbol', primary: true, render: (p) => <span className="font-medium text-gray-900 dark:text-white">{p.symbolTicker}</span> },
    { key: 'quantity', header: 'Stück', align: 'right', render: (p) => p.quantity },
    { key: 'costBasis', header: 'Einstand / Aktie', align: 'right', render: (p) => formatCurrencyCentValue(p.costBasis, p.currency) },
    { key: 'costValue', header: 'Einstandswert', align: 'right', render: (p) => formatCurrencyCentValue(Number(p.costBasis) * p.quantity, p.currency) },
    { key: 'currentPrice', header: 'Akt. Kurs', align: 'right', render: (p) => (p.currentPrice === null ? '—' : formatCurrencyCentValue(p.currentPrice, p.currency)) },
    {
      key: 'unrealizedPnL',
      header: 'Unrealisiert',
      align: 'right',
      render: (p) => (
        <span className={`font-medium ${pnlColorClass(p.unrealizedPnL)}`}>
          {p.unrealizedPnL === null ? '—' : formatCurrencyCentValue(p.unrealizedPnL, p.currency)}
        </span>
      ),
    },
    { key: 'openDate', header: 'Eröffnet', render: (p) => formatRFCDate(p.openDate) },
    {
      key: 'status',
      header: 'Status',
      render: (p) => <Badge color={p.status === 'OPEN' ? 'blue' : 'gray'}>{STOCK_STATUS_LABELS[p.status]}</Badge>,
    },
    {
      key: 'actions',
      header: 'Aktionen',
      align: 'right',
      actions: true,
      render: (p) => (
        <Button
          variant="ghost"
          size="sm"
          icon={faTrash}
          title="Aktienposition löschen"
          aria-label="Aktienposition löschen"
          disabled={busy}
          onClick={() => setDeleteTarget(p)}
        />
      ),
    },
  ];

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  return (
    <div className="space-y-6">
      <Card title="Offene Optionen" padded={false}>
        <DataTable
          bare
          columns={OPTION_COLUMNS}
          rows={openOptions}
          keyExtractor={(t) => t.id}
          loading={loading}
          emptyState={<EmptyState icon={faChartPie} title="Keine offenen Optionen" description="Sobald du eine Option eröffnest, erscheint sie hier." />}
        />
      </Card>

      <Card title="Aktienpositionen" padded={false}>
        <DataTable
          bare
          columns={stockColumns}
          rows={stockPositions}
          keyExtractor={(p) => p.id}
          loading={loading}
          emptyState={<EmptyState icon={faMoneyBillWave} title="Keine offenen Aktienpositionen" description="Zugeteilte Aktien landen automatisch in dieser Übersicht." />}
        />
      </Card>

      {deleteTarget !== null && (
        <Modal
          isOpen
          onClose={() => setDeleteTarget(null)}
          title="Aktienposition löschen"
          icon={<FontAwesomeIcon icon={faTrash} className="w-5 h-5 text-red-600 dark:text-red-400" />}
          maxWidth="sm"
          closeDisabled={busy}
        >
          <div className="p-6">
            <p className="text-gray-700 dark:text-gray-300">
              Aktienposition <span className="font-medium text-gray-900 dark:text-white">{deleteTarget.symbolTicker}</span> mit {deleteTarget.quantity} Stück wirklich löschen?
            </p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Das kann nicht rückgängig gemacht werden.</p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={busy}>Abbrechen</Button>
              <Button variant="danger" onClick={handleDeleteStock} loading={busy}>Löschen</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
